import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { UserRole, UserProfile } from '../types'

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, role?: UserRole) => Promise<void>
  signOut: () => Promise<void>
  isAdmin: () => boolean
  canEdit: () => boolean
  canAdd: () => boolean
  canViewPrices: () => boolean
  refreshSessionIfNeeded: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const initializedRef = useRef(false)
  const isSigningInRef = useRef(false)

  // Safety timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('Auth loading timeout reached, forcing loading to false');
      setLoading(false);
      initializedRef.current = true;
    }, 5000); // Reduced to 5 seconds

    return () => clearTimeout(timeout);
  }, [])

  const loadUserProfile = useCallback(async (userId: string): Promise<void> => {
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Failed to load user profile:', error.message);
        setUserProfile(null);
        return;
      }

      if (!profile) {
        console.error('No user profile found for user:', userId);
        setUserProfile(null);
        return;
      }

      setUserProfile(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
      setUserProfile(null);
    }
  }, [setUserProfile])

  const handleAuthStateChange = useCallback(async (session: Session | null) => {
    
    setSession(session)
    setUser(session?.user ?? null)
    
    if (session?.user) {
      await loadUserProfile(session.user.id)
    } else {
      setUserProfile(null)
    }
    
    if (!initializedRef.current) {
      setLoading(false)
      initializedRef.current = true
    }
  }, [loadUserProfile])

  // Manually refresh session when needed (e.g., when making API calls)
  const refreshSessionIfNeeded = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session && session.expires_at) {
        const expiresAt = session.expires_at * 1000; // Convert to milliseconds
        const timeUntilExpiry = expiresAt - Date.now();
        
        // Refresh if less than 5 minutes until expiry
        if (timeUntilExpiry < 300000) {
          const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError) {
            console.error('Failed to refresh session:', refreshError);
            return false;
          }
          
          if (newSession) {
            setSession(newSession);
            return true;
          }
        }
      }
      
      return true; // Session is still valid
    } catch (error) {
      console.error('Error checking session:', error);
      return false;
    }
  }, [])

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error getting initial session:', error);
        }
        
        if (mounted) {
          await handleAuthStateChange(session)
        }
             } catch (error) {
         console.error('Error initializing auth:', error);
         if (mounted) {
           setLoading(false)
           initializedRef.current = true
         }
       }
    }

    // Initialize auth
    initializeAuth()

         // Listen for auth changes
     const {
       data: { subscription },
     } = supabase.auth.onAuthStateChange(async (event, session) => {
       
       // Handle SIGNED_IN events from actual login attempts
       if (event === 'SIGNED_IN' && isSigningInRef.current && mounted) {
         isSigningInRef.current = false; // Reset the flag
         await handleAuthStateChange(session);
         return;
       }
       
       // Ignore TOKEN_REFRESHED and SIGNED_IN events that occur when tab regains focus
       // These don't represent actual auth state changes
       if ((event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') && initializedRef.current) {
         return;
       }
       
       // Always handle SIGNED_OUT events immediately, regardless of initialization state
       if (event === 'SIGNED_OUT' && mounted) {
         await handleAuthStateChange(null)
       }
       // Handle INITIAL_SESSION or actual auth changes
       else if ((event === 'INITIAL_SESSION' || event === 'USER_UPDATED') && mounted) {
         await handleAuthStateChange(session)
       }
     })

    return () => {
      mounted = false;
      subscription.unsubscribe();
    }
  }, [handleAuthStateChange, refreshSessionIfNeeded])

  const signIn = async (email: string, password: string) => {
    try {
      // Set flag to indicate we're actively signing in
      isSigningInRef.current = true
      
      // Safety timeout to reset flag if auth state change doesn't fire
      const timeoutId = setTimeout(() => {
        if (isSigningInRef.current) {
          console.warn('Sign-in flag timeout - resetting');
          isSigningInRef.current = false;
        }
      }, 10000); // 10 second timeout
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        clearTimeout(timeoutId);
        isSigningInRef.current = false
        throw error
      }
      
      // The auth state change listener will handle updating the UI
      // and will reset the flag after processing
      
      // Store timeout in a way that won't block
      setTimeout(() => clearTimeout(timeoutId), 15000);
    } catch (error) {
      isSigningInRef.current = false
      throw error
    }
  }

  const signUp = async (email: string, password: string, role: UserRole = 'user') => {
    try {
      // Set flag to indicate we're actively signing in/up
      isSigningInRef.current = true
      
      // Safety timeout to reset flag if auth state change doesn't fire
      const timeoutId = setTimeout(() => {
        if (isSigningInRef.current) {
          console.warn('Sign-up flag timeout - resetting');
          isSigningInRef.current = false;
        }
      }, 10000); // 10 second timeout
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
      })
      
      if (error) {
        clearTimeout(timeoutId);
        isSigningInRef.current = false
        throw error
      }
      
      // Note: The database trigger will automatically create the user profile
      
      // Store timeout in a way that won't block
      setTimeout(() => clearTimeout(timeoutId), 15000);
    } catch (error) {
      isSigningInRef.current = false
      throw error
    }
  }

  const signOut = async () => {
    try {
      
      // Check if we have a valid session before attempting to sign out
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (currentSession) {
        
        // Sign out from Supabase with a timeout - use local scope to avoid server issues
        const signOutPromise = (async () => {
          return await supabase.auth.signOut({ scope: 'local' });
        })();
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => {
            console.warn('Sign out promise timed out');
            reject(new Error('Sign out timeout'));
          }, 10000)
        );
        
        const result: any = await Promise.race([signOutPromise, timeoutPromise]);
        const { error } = result || {};
        
                 if (error) {
           console.error('Supabase sign out error:', error);
           // Don't throw the error if it's an "Auth session missing" error
           // as this is expected when the session has already expired
           if (!(error instanceof Error && error.message?.includes('Auth session missing'))) {
             throw error;
           }
         }
      }

      // Clear local auth state immediately so UI updates without waiting for listener
      setUserProfile(null);
      setUser(null);
      setSession(null);

      // Manually remove Supabase auth token(s) from localStorage as a fallback
      try {
        // Clear the custom storage key used in supabase.ts
        localStorage.removeItem('mc53-auth');
        
        // Also clear any default Supabase keys as fallback
        Object.keys(localStorage)
          .filter((key) => key.startsWith('sb-'))
          .forEach((key) => localStorage.removeItem(key));
      } catch (storageError) {
        console.warn('Failed to clear Supabase auth tokens from localStorage:', storageError);
      }
      
      // Force refresh the session to ensure it's cleared
      try {
        await supabase.auth.getSession();
      } catch (sessionError) {
        console.warn('Failed to refresh session after logout:', sessionError);
      }
      
    } catch (error) {
      console.error('Sign out failed:', error);
      // Even if Supabase fails, clear local state as fallback
      setUserProfile(null);
      setUser(null);
      setSession(null);
      
      // Also clear localStorage tokens as fallback
      try {
        localStorage.removeItem('mc53-auth');
        Object.keys(localStorage)
          .filter((key) => key.startsWith('sb-'))
          .forEach((key) => localStorage.removeItem(key));
      } catch (storageError) {
        console.warn('Failed to clear localStorage in error handler:', storageError);
      }
      
      // Don't throw the error if it's an "Auth session missing" error
      // as this is expected when the session has already expired
      if (!(error instanceof Error && error.message?.includes('Auth session missing'))) {
        throw error;
      }
    }
  }

  const isAdmin = () => {
    return userProfile?.role === 'admin'
  }

  const canEdit = () => {
    return userProfile?.role === 'admin'
  }

  const canAdd = () => {
    return userProfile?.role === 'admin' || userProfile?.role === 'admin_light'
  }

  const canViewPrices = () => {
    return userProfile?.role === 'admin' || userProfile?.role === 'admin_light'
  }

  const value = {
    user,
    userProfile,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    isAdmin,
    canEdit,
    canAdd,
    canViewPrices,
    refreshSessionIfNeeded,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
